import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Search, FileSignature, Banknote } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const steps = [
    { n: "01", icon: ClipboardList, title: "Submit", desc: "Send a one-page app + 4 months of bank statements. ISOs use our portal; merchants apply directly. Takes 60 seconds." },
    { n: "02", icon: Search, title: "Underwrite", desc: "A real underwriter reviews your file in under 4 hours. We pull alt-data, verify deposits, and structure a real offer — not a stipulation maze." },
    { n: "03", icon: FileSignature, title: "Approve & Sign", desc: "You get a clean, transparent offer with full term breakdown. eSign in minutes. No surprise fees, no buried clauses." },
    { n: "04", icon: Banknote, title: "Fund", desc: "Wire hits the merchant's account in 24 hours. ISOs get commissioned the next business day. Done." },
];

export default function HowItWorks() {
    usePageTitle("How It Works", "From submission to wire in 24 hours. Four steps: Submit, Underwrite, Approve, Fund. No phone trees. No stipulation chasing.");
    return (
        <>
            <section className="bg-white relative overflow-hidden" data-testid="how-hero">
                <div className="absolute inset-0 bg-grid pointer-events-none" />
                <div className="container-lended relative pt-20 pb-12 md:pt-28 max-w-4xl">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="divider-orange" />
                        <span className="eyebrow">How It Works</span>
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.04em] leading-[0.95]">
                        From submission to wire <br /> in <span className="text-[#FF6B1A]">24 hours.</span>
                    </h1>
                    <p className="mt-7 text-lg md:text-xl text-black/70">
                        Four steps. No phone trees. No stipulation chasing. No 8-day "approval pending" purgatory.
                    </p>
                </div>
            </section>

            <section className="bg-white pb-24" data-testid="how-steps">
                <div className="container-lended">
                    <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
                        {steps.map((s, i) => (
                            <div key={s.n} className={`relative ${i % 2 === 1 ? "md:mt-16" : ""}`} data-testid={`step-${s.n}`}>
                                <div className="flex items-start gap-6">
                                    <div className="font-display font-extrabold text-7xl md:text-8xl text-[#FF6B1A]/15 leading-none shrink-0">{s.n}</div>
                                    <div className="pt-2">
                                        <s.icon className="text-[#FF6B1A]" size={28} />
                                        <h3 className="font-display text-3xl font-bold mt-3 leading-tight">{s.title}.</h3>
                                        <p className="mt-3 text-black/70 leading-relaxed">{s.desc}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#F8F5F2] py-20" data-testid="how-requirements">
                <div className="container-lended grid lg:grid-cols-2 gap-12">
                    <div>
                        <span className="eyebrow">Minimum Requirements</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1]">
                            What you need to qualify.
                        </h2>
                    </div>
                    <div className="space-y-5">
                        {[
                            ["6+ months in business", "Demonstrated operational history."],
                            ["$50,000+ monthly revenue", "Standard revenue floor; higher unlocks better pricing."],
                            ["Business bank account", "Active deposit account in the business name."],
                            ["U.S.-based business", "Including territories. Canada available on select programs."],
                        ].map(([t, d]) => (
                            <div key={t} className="flex items-start gap-4 border-b border-black/10 pb-5">
                                <div className="w-6 h-6 bg-[#FF6B1A] text-white text-xs font-bold flex items-center justify-center mt-1">✓</div>
                                <div>
                                    <div className="font-display font-bold text-lg">{t}</div>
                                    <p className="text-sm text-black/60 mt-1">{d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#0A0A0A] text-white py-16" data-testid="how-cta">
                <div className="container-lended flex flex-col md:flex-row items-center justify-between gap-6">
                    <h3 className="font-display text-3xl md:text-4xl font-bold leading-[1] max-w-xl">Stop chasing. Start closing.</h3>
                    <Link to="/#apply" className="btn-orange">Apply Now <ArrowRight size={18} /></Link>
                </div>
            </section>
        </>
    );
}
